/* eslint-disable @typescript-eslint/naming-convention */
import React, { memo, useMemo } from 'react';
import classNames from 'classnames';
import { Button } from '@ui';
import { NODE_DEPTH_ATTR, NODE_DRAG_DISABLED_ATTR, NODE_ID_ATTR, NODE_LAST_SIBLING_ATTR, NODE_POSITION_ATTR } from './useTreeDragDrop';
import { selectCanDrop, selectDropPosition, selectIsDragging, selectIsDraggedOver, selectIsExpanded } from './useTreeStore';
/** Attribute used by the container click handler to locate expand toggles. */
export const TOGGLE_EXPAND_ATTR = 'data-toggle-expand';
/**
 * Attribute carrying the id of the node whose expansion state should be
 * toggled. Set on the counter-row toggle button so click delegation can
 * resolve the target without walking up to a `data-node-id` element — this
 * keeps counter rows out of the drag-and-drop `closest('[data-node-id]')`
 * lookup, which is scoped to real draggable node rows.
 */
export const TOGGLE_TARGET_ID_ATTR = 'data-toggle-target-id';
/** Attribute used by the container click handler to locate plus buttons. */
export const PLUS_BUTTON_ATTR = 'data-plus-button';
/** Attribute used by the container click handler to locate add-item buttons. */
export const ADD_ITEM_ATTR = 'data-add-item';
function DraggableTreeNodeImpl({ node, store, itemRenderer, showPlusButton = false, dragHandleClassName = 'tree-drag-handle', itemClassName = 'tree-item', maxDepth }) {
    var _a, _b, _c, _d;
    // Selective per-node subscriptions — only re-renders when this node's slice changes.
    const isExpanded = store(selectIsExpanded(node.id));
    const isDragging = store(selectIsDragging(node.id));
    const isDraggedOver = store(selectIsDraggedOver(node.id));
    const dropPosition = store(selectDropPosition(node.id));
    const canDrop = store(selectCanDrop(node.id));
    const isDragDisabled = (_a = node.isDragDisabled) !== null && _a !== void 0 ? _a : false;
    const isAddDisabled = node.addDisabledReason != null;
    const isLastSibling = (_b = node.isLastSibling) !== null && _b !== void 0 ? _b : false;
    const hasChildren = (_c = node.hasChildren) !== null && _c !== void 0 ? _c : false;
    const isLastInGroup = (_d = node.isLastInGroup) !== null && _d !== void 0 ? _d : false;
    const rendererOptions = useMemo(() => ({
        isDragging,
        isOver: isDraggedOver,
        canDrop,
        depth: node.depth,
        hasChildren,
        isExpanded,
        // Expand toggling is handled via event delegation on the container; we
        // still hand consumers a no-op `onToggleExpand` so existing renderers
        // that bind their own toggle UI remain compatible.
        onToggleExpand: () => store.getState().toggleExpand(node.id),
        onPlusClick: undefined,
        onAddItem: undefined,
        showPlusButton
    }), [isDragging, isDraggedOver, canDrop, node.depth, hasChildren, isExpanded, node.id, showPlusButton, store]);
    const rowClasses = classNames('tree-node', itemClassName, {
        'tree-node--has-children': hasChildren,
        'tree-node--last-sibling': isLastSibling,
        'tree-node--last-in-group': isLastInGroup,
        'tree-node--expanded': isExpanded,
        'tree-node--drag-disabled': isDragDisabled,
        'tree-node--drop-before': dropPosition === 'before',
        'tree-node--drop-after': dropPosition === 'after'
    });
    const contentClasses = classNames('tree-node__content', `tree-node__content--depth-${node.depth}`, {
        'tree-node--drop-inside': dropPosition === 'inside',
        'tree-node--dragging': isDragging,
        'tree-node--can-drop': canDrop,
        'tree-node--drag-over': isDraggedOver
    });
    // Depth indicator lines are rendered by CSS via pseudo-elements driven by
    // the `--node-depth` custom property on the row wrapper — zero extra DOM
    // nodes regardless of tree depth. See DraggableTreeViewStyles.scss (task-05).
    const dragHandle = !isDragDisabled ? (React.createElement("div", { className: classNames('tree-node__drag-handle', dragHandleClassName) },
        React.createElement("i", { className: "fa-light fa-grip-dots-vertical" }))) : undefined;
    // NOTE: The row carries `data-node-*` attributes — the container-level DnD
    // and click handlers walk up via `closest()` to resolve the node, so no
    // per-node event listeners are attached here (the event-delegation win).
    // `--node-depth` drives indentation + pseudo-element depth lines in SCSS.
    const rowStyle = {
        ['--node-depth']: String(node.depth)
    };
    return (React.createElement("div", { className: rowClasses, style: rowStyle, [NODE_ID_ATTR]: String(node.id), [NODE_DEPTH_ATTR]: String(node.depth), [NODE_POSITION_ATTR]: node.position !== undefined ? String(node.position) : '', [NODE_LAST_SIBLING_ATTR]: String(isLastSibling), [NODE_DRAG_DISABLED_ATTR]: String(isDragDisabled) },
        isDraggedOver && dropPosition === 'before' && (React.createElement("div", { className: "tree-node__drop-indicator tree-node__drop-indicator--before" })),
        React.createElement("div", { className: contentClasses, draggable: !isDragDisabled },
            React.createElement("div", { className: "tree-node__item-content" }, itemRenderer(node, rendererOptions, dragHandle)),
            showPlusButton && node.depth < maxDepth - 1 && !node.hideAddButton && (React.createElement(Button, { variant: "secondary", className: "tree-node__plus-button", "aria-label": "Add child", disabled: isAddDisabled, title: node.addDisabledReason, [ADD_ITEM_ATTR]: 'true' },
                React.createElement("i", { className: "fa-light fa-plus" })))),
        isDraggedOver && dropPosition === 'after' && (React.createElement("div", { className: "tree-node__drop-indicator tree-node__drop-indicator--after" }))));
}
/**
 * `DraggableTreeNode` is memoised so that — together with Zustand's selective
 * subscriptions — only the node whose slice of state changed actually
 * re-renders. The default shallow comparator is sufficient: the flattened
 * list preserves node object references across renders, so props equality
 * holds unless the node itself (or a sibling-affecting attribute) changes.
 */
export const DraggableTreeNode = memo(DraggableTreeNodeImpl);
